import { Component, computed, effect, inject, Input } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ColorsetDropdownComponent } from '../../atoms/colorset-dropdown/colorset-dropdown.component';
import { ColorsComponent } from '../../atoms/colors/colors.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import {
  ColorsetDefaultKey,
  ColorsetDefaultsService,
  ColorsetFallback,
} from '../../../services/colorset/colorset-defaults.service';
import { ColorsetStore } from '../../../stores/colorset/colorset.state';
import { fallbackColorset } from '../../../models/colorset';

/**
 * Overview of all commune-wide default colorsets (general, map and per chart
 * type) with inline editing. Empty slots show their fallback as placeholder.
 */
@Component({
  selector: 'app-colorset-defaults',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ColorsetDropdownComponent,
    ColorsComponent,
    HeaderComponent,
  ],
  templateUrl: './colorset-defaults.component.html',
})
export class ColorsetDefaultsComponent {
  @Input() readonly = false;

  readonly defaultsService = inject(ColorsetDefaultsService);
  readonly colorsetStore = inject(ColorsetStore);
  readonly slots = this.defaultsService.slots;

  readonly form = new FormGroup(
    Object.fromEntries(
      this.slots.map(slot => [slot.key, new FormControl<string | null>(null)])
    ) as Record<ColorsetDefaultKey, FormControl<string | null>>
  );

  private readonly systemFallback: ColorsetFallback = {
    colorset: fallbackColorset,
    source: 'Systemstandard',
  };

  /**
   * Fallback of the chart/map slots: the general default, otherwise the
   * system default.
   */
  private readonly inheritedFallback = computed((): ColorsetFallback => {
    const general = this.colorsetOf('general');
    return general
      ? { colorset: general, source: 'Kommunen-Standard (Allgemein)' }
      : this.systemFallback;
  });

  /** Colorset that applies while the given slot is empty. */
  fallbackOf(key: ColorsetDefaultKey): ColorsetFallback {
    return key === 'general' ? this.systemFallback : this.inheritedFallback();
  }

  constructor() {
    effect(() => {
      this.form.patchValue(this.defaultsService.defaults(), {
        emitEvent: false,
      });
    });

    for (const slot of this.slots) {
      this.form.controls[slot.key].valueChanges.subscribe(value => {
        if (
          (value || null) ===
          (this.defaultsService.defaults()[slot.key] || null)
        )
          return;
        this.defaultsService.setDefault(slot.key, value || null);
      });
    }
  }

  colorsetOf(key: ColorsetDefaultKey) {
    const id = this.defaultsService.defaults()[key];
    return id ? this.colorsetStore.getById(id) : null;
  }
}
